import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SocketProvider } from "./context/SocketContext";

import { ThemeProvider } from "./context/ThemeContext";

// Shared pages
import Login from "./pages/Login";
import Register from "./pages/Register";

// Seller pages
import SellerHome from "./pages/seller/Home";
import NewOrder from "./pages/seller/NewOrder";
import OrderTracking from "./pages/seller/OrderTracking";
import MyDeliveries from "./pages/seller/MyDeliveries";
import SellerAccount from "./pages/seller/Account";
import SellerFeedback from "./pages/seller/Feedback";

// Rider pages
import RiderHome from "./pages/rider/Home";
import ActiveTrip from "./pages/rider/ActiveTrip";
import Earnings from "./pages/rider/Earnings";
import RiderFeedback from "./pages/rider/Feedback";

// Components
import ProtectedRoute from "./components/ProtectedRoute";
import BottomNav from "./components/BottomNav";
import RiderBottomNav from "./components/RiderBottomNav";
import InstallPrompt from "./components/InstallPrompt";

function SellerLayout({ children }) {
  return (
    <div className="pb-20">
      {children}
      <BottomNav />
    </div>
  );
}

function RiderLayout({ children }) {
  return (
    <div className="pb-20">
      {children}
      <RiderBottomNav />
    </div>
  );
}

function AppRoutes() {
  const { user, activeRole, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <img
            src={`${process.env.PUBLIC_URL}/byka_icon.png`}
            alt="Byka logo"
            className="w-16 h-16 mx-auto mb-4 object-contain"
          />
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500 mx-auto" />
        </div>
      </div>
    );
  }

  const homeRedirect = activeRole === "rider" ? "/rider" : "/seller";

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to={homeRedirect} replace /> : <Login />}
        />
        <Route
          path="/register"
          element={user ? <Navigate to={homeRedirect} replace /> : <Register />}
        />

        {/* Seller routes */}
        <Route
          path="/seller"
          element={
            <ProtectedRoute allowedRole="seller">
              <SellerLayout>
                <SellerHome />
              </SellerLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/seller/new-order"
          element={
            <ProtectedRoute allowedRole="seller">
              <SellerLayout>
                <NewOrder />
              </SellerLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/seller/order/:id"
          element={
            <ProtectedRoute allowedRole="seller">
              <SellerLayout>
                <OrderTracking />
              </SellerLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/seller/deliveries"
          element={
            <ProtectedRoute allowedRole="seller">
              <SellerLayout>
                <MyDeliveries />
              </SellerLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/seller/account"
          element={
            <ProtectedRoute allowedRole="seller">
              <SellerLayout>
                <SellerAccount />
              </SellerLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/seller/feedback"
          element={
            <ProtectedRoute allowedRole="seller">
              <SellerFeedback />
            </ProtectedRoute>
          }
        />

        {/* Rider routes */}
        <Route
          path="/rider"
          element={
            <ProtectedRoute allowedRole="rider">
              <SocketProvider>
                <RiderLayout>
                  <RiderHome />
                </RiderLayout>
              </SocketProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/rider/trip"
          element={
            <ProtectedRoute allowedRole="rider">
              <SocketProvider>
                <RiderLayout>
                  <ActiveTrip />
                </RiderLayout>
              </SocketProvider>
            </ProtectedRoute>
          }
        />
        <Route
          path="/rider/earnings"
          element={
            <ProtectedRoute allowedRole="rider">
              <RiderLayout>
                <Earnings />
              </RiderLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/rider/feedback"
          element={
            <ProtectedRoute allowedRole="rider">
              <RiderLayout>
                <RiderFeedback />
              </RiderLayout>
            </ProtectedRoute>
          }
        />

        {/* Default roots */}
        <Route
          path="/"
          element={<Navigate to={user ? homeRedirect : "/login"} replace />}
        />
        <Route
          path="*"
          element={<Navigate to={user ? homeRedirect : "/login"} replace />}
        />
      </Routes>

      {user && <InstallPrompt />}
    </>
  );
}

import { Toaster } from "react-hot-toast";

export default function App() {
  // Capacitor Android builds pass PUBLIC_URL=./, which CRA bakes in as ".".
  // React Router needs an absolute path or an empty string, so treat any
  // relative value ("." or "./") as the root.
  const rawPublicUrl = process.env.PUBLIC_URL || "";
  const basename =
    rawPublicUrl === "." || rawPublicUrl === "./" ? "" : rawPublicUrl;

  return (
    <BrowserRouter basename={basename}>
      <Toaster position="top-center" toastOptions={{ duration: 4000 }} />
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
